import React, { createContext, useContext, useRef, useState, useEffect, useCallback } from 'react';
import { useAction } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { useAuth } from '@/context/AuthContext';
import * as Sentry from '@sentry/react';
import {
  generateEncryptionKey,
  exportKey,
  importKey,
  encryptEntry,
  decryptEntry,
  encryptChatMessages,
  decryptChatMessages,
  encrypt,
  decrypt,
  generateRecoveryKey,
  generateRecoverySalt,
  deriveKekFromRecovery,
  wrapDataKey,
  unwrapDataKey,
} from '@/utils/crypto';
import { getCachedDataKey, cacheDataKey, clearCachedDataKey } from '@/utils/keyStore';

export type EncryptionStatus =
  | 'loading'
  | 'ready'
  | 'error'
  | 'no-user'
  | 'setup-recovery'       // private key just created; show recovery key to save
  | 'needs-recovery-key';  // private user on a new device must enter recovery key

export type PrivacyMode = 'standard' | 'e2ee';

interface EncryptionContextType {
  status: EncryptionStatus;
  mode: PrivacyMode | null;
  /** Populated only during 'setup-recovery' — the key to show the user once. */
  recoveryKey: string | null;
  retry: () => void;
  confirmRecoverySaved: () => void;
  unlockWithRecoveryKey: (recoveryKey: string) => Promise<void>;
  upgradeToE2ee: () => Promise<void>;
  resetEncryption: () => Promise<void>;
  /** Dev-only: revert private → standard without losing data. */
  devRevertToStandard: () => Promise<void>;
  encryptEntry: (entry: Parameters<typeof encryptEntry>[0]) => Promise<ReturnType<typeof encryptEntry> extends Promise<infer R> ? R : never>;
  decryptEntry: (entry: Parameters<typeof decryptEntry>[0]) => Promise<ReturnType<typeof decryptEntry> extends Promise<infer R> ? R : never>;
  encryptChatMessages: (messages: Parameters<typeof encryptChatMessages>[0]) => Promise<ReturnType<typeof encryptChatMessages> extends Promise<infer R> ? R : never>;
  decryptChatMessages: (messages: Parameters<typeof decryptChatMessages>[0]) => Promise<ReturnType<typeof decryptChatMessages> extends Promise<infer R> ? R : never>;
  encrypt: (plaintext: string) => Promise<string>;
  decrypt: (ciphertext: string) => Promise<string>;
}

const EncryptionContext = createContext<EncryptionContextType | undefined>(undefined);

export const EncryptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const keyRef = useRef<CryptoKey | null>(null);
  // Recovery-wrapped key + salt held between init and unlock on a new device.
  const pendingWrapped = useRef<{ wrappedKey: string; salt: string } | null>(null);
  const [status, setStatus] = useState<EncryptionStatus>('loading');
  const [mode, setMode] = useState<PrivacyMode | null>(null);
  const [recoveryKey, setRecoveryKey] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  const getKeyAction = useAction(api.userKeys.getKey);
  const storeKeyAction = useAction(api.userKeys.storeKey);
  const upgradeAction = useAction(api.userKeys.upgradeToE2ee);
  const resetAction = useAction(api.userKeys.resetEncryption);
  const devRevertAction = useAction(api.userKeys.devRevertToStandard);

  useEffect(() => {
    if (!user) {
      keyRef.current = null;
      pendingWrapped.current = null;
      setMode(null);
      setStatus('no-user');
      return;
    }

    let cancelled = false;
    setStatus('loading');

    const init = async () => {
      try {
        // The SERVER is authoritative for the mode (not the local cache): a
        // revert/upgrade on another device must be reflected here. So ask the
        // server first, and use the device cache only as the key source for
        // private mode.
        const result = await getKeyAction({});
        if (cancelled) return;

        if (!result) {
          // Brand-new user: silently provision a standard key and land them in
          // the app. Private max stays opt-in via Settings (upgradeToE2ee).
          const dataKey = await generateEncryptionKey();
          const exported = await exportKey(dataKey);
          await storeKeyAction({ encryptionKey: exported, mode: 'standard' });
          if (cancelled) return;
          keyRef.current = dataKey;
          setMode('standard');
          setStatus('ready');
          return;
        }

        if (result.protection === 'server') {
          // Standard mode. Clear any stale private-mode cache (e.g. left over
          // after a revert that happened on a different device).
          await clearCachedDataKey(user.id);
          keyRef.current = await importKey(result.key);
          setMode('standard');
          setStatus('ready');
          return;
        }

        // Private mode: use the cached data key if this device has one,
        // otherwise require the recovery key.
        const cached = await getCachedDataKey(user.id);
        if (cancelled) return;
        if (cached) {
          keyRef.current = cached;
          setMode('e2ee');
          setStatus('ready');
          return;
        }

        pendingWrapped.current = { wrappedKey: result.wrappedKey, salt: result.salt };
        setMode('e2ee');
        setStatus('needs-recovery-key');
      } catch (error) {
        if (cancelled) return;
        console.error('Encryption key initialization failed:', error);
        Sentry.captureException(error, { tags: { component: 'EncryptionContext' } });
        setStatus('error');
      }
    };

    init();

    return () => {
      cancelled = true;
    };
    // Depend on the stable user id, NOT the `user` object: AuthContext rebuilds
    // `user` on every render, and re-running init would find the just-cached key
    // and skip the 'setup-recovery' screen (losing the recovery key on native).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, retryCount]);

  const retry = useCallback(() => {
    setStatus('loading');
    setRetryCount((c) => c + 1);
  }, []);

  const confirmRecoverySaved = useCallback(() => {
    setRecoveryKey(null);
    setStatus('ready');
  }, []);

  const unlockWithRecoveryKey = useCallback(
    async (rk: string) => {
      if (!user) throw new Error('No user');
      const pending = pendingWrapped.current;
      if (!pending) throw new Error('Nothing to unlock');
      // Throws if the recovery key is wrong (GCM auth failure).
      const kek = await deriveKekFromRecovery(rk, pending.salt);
      const dataKey = await unwrapDataKey(pending.wrappedKey, kek);
      await cacheDataKey(user.id, dataKey);
      keyRef.current = dataKey;
      pendingWrapped.current = null;
      setMode('e2ee');
      setStatus('ready');
    },
    [user]
  );

  const upgradeToE2ee = useCallback(async () => {
    if (!user || !keyRef.current) throw new Error('Encryption not ready');
    const rk = generateRecoveryKey();
    const salt = generateRecoverySalt();
    const kek = await deriveKekFromRecovery(rk, salt);
    const wrapped = await wrapDataKey(keyRef.current, kek);
    await upgradeAction({ wrappedKey: wrapped, recoverySalt: salt });
    await cacheDataKey(user.id, keyRef.current);
    setMode('e2ee');
    setRecoveryKey(rk);
    setStatus('setup-recovery');
  }, [user, upgradeAction]);

  const resetEncryption = useCallback(async () => {
    if (!user) throw new Error('No user');
    await resetAction({});
    await clearCachedDataKey(user.id);
    keyRef.current = null;
    pendingWrapped.current = null;
    setMode(null);
    setStatus('loading');
    setRetryCount((c) => c + 1);
  }, [user, resetAction]);

  const devRevertToStandard = useCallback(async () => {
    if (!user || !keyRef.current) throw new Error('Encryption not ready');
    const exported = await exportKey(keyRef.current);
    await devRevertAction({ encryptionKey: exported });
    await clearCachedDataKey(user.id);
    setMode('standard');
    setStatus('ready');
  }, [user, devRevertAction]);

  const getKey = useCallback((): CryptoKey => {
    if (!keyRef.current) {
      throw new Error('Encryption key not ready');
    }
    return keyRef.current;
  }, []);

  const value: EncryptionContextType = {
    status,
    mode,
    recoveryKey,
    retry,
    confirmRecoverySaved,
    unlockWithRecoveryKey,
    upgradeToE2ee,
    resetEncryption,
    devRevertToStandard,
    encryptEntry: useCallback(async (entry) => encryptEntry(entry, getKey()), [getKey]),
    decryptEntry: useCallback(async (entry) => decryptEntry(entry, getKey()), [getKey]),
    encryptChatMessages: useCallback(async (messages) => encryptChatMessages(messages, getKey()), [getKey]),
    decryptChatMessages: useCallback(async (messages) => decryptChatMessages(messages, getKey()), [getKey]),
    encrypt: useCallback(async (plaintext) => encrypt(plaintext, getKey()), [getKey]),
    decrypt: useCallback(async (ciphertext) => decrypt(ciphertext, getKey()), [getKey]),
  };

  return (
    <EncryptionContext.Provider value={value}>
      {children}
    </EncryptionContext.Provider>
  );
};

export const useEncryption = () => {
  const context = useContext(EncryptionContext);
  if (context === undefined) {
    throw new Error('useEncryption must be used within an EncryptionProvider');
  }
  return context;
};
